import { bundleCompareAt, bundlePrice, offersFor, type BundleOffer } from '../../commerce/offers';
import type { ProductVariant, SellingPlan, ShopProduct } from '../../commerce/shopify.types';
import { etoiles } from '../product-badges/product-badges';

/**
 * Le bloc « produit » de la fiche, dans l'ordre exact de chien-heureux.fr :
 *
 *   1. les avis
 *   2. le titre
 *   3. le badge, s'il y en a un
 *   4. les onglets Description, Livraison, Garantie
 *   5. l'offre : lots Moon Bundles, ou déclinaisons Shopify
 *   6. le bouton d'ajout au panier
 *   7. les deux lignes de réassurance, paiement puis livraison
 *   8. la fiche en clair
 *
 * Rien n'est inventé : la description est la description courte rédigée dans
 * Shopify, la garantie vient de son metafield, les lots viennent de Moon
 * Bundles. Seule la livraison, identique sur toute la boutique, est écrite ici.
 */

export interface OfferSelection {
  variant: ProductVariant;
  /** Nombre d'exemplaires : un lot Moon Bundles en commande plusieurs. */
  quantite: number;
  /** Libellé du lot choisi, pour nommer la ligne de panier. */
  lot?: string;
  /** Renseigné seulement si le visiteur a coché l'abonnement. */
  sellingPlan?: SellingPlan;
}

export interface ProductOffer {
  element: HTMLElement;
  selection(): OfferSelection;
}

export function formatMoney(montant: number | string, devise = 'EUR'): string {
  const valeur = typeof montant === 'number' ? montant : Number.parseFloat(montant);
  if (Number.isNaN(valeur)) return '';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(
    valeur,
  );
}

export function variantsOf(product: ShopProduct): ProductVariant[] {
  if (product.variants?.length) return product.variants;
  return [
    {
      id: product.variantId,
      title: product.title,
      price: product.price,
      availableForSale: product.availableForSale,
    },
  ];
}

/**
 * Fenêtre de livraison annoncée : préparation le jour même jusqu'à midi, puis
 * deux jours ouvrés. C'est la promesse du site, elle ne dépend pas du produit.
 */
export function deliveryWindow(maintenant: Date): string {
  const debut = new Date(maintenant);
  debut.setDate(debut.getDate() + (maintenant.getHours() < 12 ? 2 : 3));
  const fin = new Date(debut);
  fin.setDate(fin.getDate() + 1);
  const format = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
  return `entre le ${format.format(debut)} et le ${format.format(fin)}`;
}

/** Livraison : le seul contenu éditorial, commun à toute la boutique. */
const LIVRAISON = [
  'Expédition sous 24 h ouvrées depuis la France.',
  'Point relais 3,90 €, à domicile 5,90 €, offerte dès 49 € d’achat.',
  'Suivi envoyé par e-mail dès la remise au transporteur.',
];

/**
 * Bandeau d'avis, en tête de fiche comme sur le site.
 *
 * Il réutilise les étoiles des repères produit, remplies au demi-point près par
 * un dégradé, plutôt qu'une suite de caractères : une demi-étoile écrite en
 * texte dépend d'un glyphe que toutes les polices n'ont pas, et s'affichait ici
 * en rectangle plein.
 */
export function createReviews(product: ShopProduct): HTMLElement | null {
  const note = product.details?.note;
  const nombre = product.details?.nombreAvis;
  if (note === undefined) return null;

  const bloc = document.createElement('p');
  bloc.className = 'offer__reviews';

  const texte = document.createElement('span');
  texte.className = 'offer__reviews-text';
  const valeur = note.toFixed(1).replace('.', ',');
  texte.textContent = nombre !== undefined ? `${valeur} sur 5 · ${nombre} avis` : `${valeur} sur 5`;

  bloc.append(etoiles(note), texte);
  bloc.setAttribute('aria-label', texte.textContent.replace('·', ','));
  return bloc;
}

/**
 * Badge de la fiche. Sur le site c'est un bloc de thème rédigé produit par
 * produit, hors API. Quand un lot Moon Bundles existe, son premier palier porte
 * la même promesse, « 1 Pack (≈4-6 semaines) » : on la reprend telle quelle
 * plutôt que d'inventer une accroche.
 */
export function createBadge(product: ShopProduct): HTMLElement | null {
  const premier = offersFor(product.handle)[0];
  if (!premier?.titre) return null;
  const badge = document.createElement('p');
  badge.className = 'offer__badge';
  badge.textContent = premier.titre.replace(/[()]/g, '').replace('≈', '= ');
  return badge;
}

function ajouterLignes(zone: HTMLElement, texte: string): void {
  for (const ligne of texte.split('\n')) {
    const propre = ligne.trim();
    if (!propre) continue;
    const p = document.createElement('p');
    p.textContent = propre;
    zone.append(p);
  }
}

/** Construit le bloc d'achat et renvoie de quoi lire la sélection. */
export function createProductOffer(product: ShopProduct): ProductOffer {
  const element = document.createElement('div');
  element.className = 'offer';

  const variants = variantsOf(product);
  const defaut = variants[0] as ProductVariant;
  let choisi: ProductVariant = variants.find((v) => v.availableForSale) ?? defaut;
  const lots = offersFor(product.handle);
  let lotChoisi: BundleOffer | null = lots.find((l) => l.misEnAvant) ?? lots[0] ?? null;
  let abonnement = false;

  // --- 4. onglets Description, Livraison, Garantie ------------------------
  const onglets = document.createElement('div');
  onglets.className = 'offer__tabs';
  const barre = document.createElement('div');
  barre.className = 'offer__tablist';
  barre.setAttribute('role', 'tablist');
  const panneaux = document.createElement('div');
  panneaux.className = 'offer__panels';

  // La description est la description COURTE rédigée dans Shopify, celle qui
  // est affichée sur la fiche du site. La description longue, tronquée par
  // l'API, ne raconte pas la même chose et ne doit pas la remplacer.
  const description = product.details?.descriptionCourte?.trim() || product.description;

  const contenus: { id: string; titre: string; lignes: string[] }[] = [
    { id: 'description', titre: 'Description', lignes: description.split('\n') },
    { id: 'livraison', titre: 'Livraison', lignes: LIVRAISON },
  ];
  const garantie = product.details?.garantie?.trim();
  if (garantie) contenus.push({ id: 'garantie', titre: 'Garantie', lignes: garantie.split('\n') });

  const boutons: HTMLButtonElement[] = [];
  const zones: HTMLElement[] = [];

  contenus.forEach((contenu, index) => {
    const bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.className = 'offer__tab';
    bouton.id = `offer-tab-${contenu.id}`;
    bouton.setAttribute('role', 'tab');
    bouton.setAttribute('aria-controls', `offer-panel-${contenu.id}`);
    bouton.setAttribute('aria-selected', String(index === 0));
    bouton.tabIndex = index === 0 ? 0 : -1;
    bouton.textContent = contenu.titre;

    const zone = document.createElement('div');
    zone.className = 'offer__panel';
    zone.id = `offer-panel-${contenu.id}`;
    zone.setAttribute('role', 'tabpanel');
    zone.setAttribute('aria-labelledby', bouton.id);
    zone.hidden = index !== 0;
    ajouterLignes(zone, contenu.lignes.join('\n'));

    bouton.addEventListener('click', () => {
      boutons.forEach((autre, i) => {
        const actif = autre === bouton;
        autre.setAttribute('aria-selected', String(actif));
        autre.tabIndex = actif ? 0 : -1;
        const panneau = zones[i];
        if (panneau) panneau.hidden = !actif;
      });
    });

    boutons.push(bouton);
    zones.push(zone);
    barre.append(bouton);
    panneaux.append(zone);
  });

  onglets.append(barre, panneaux);
  element.append(onglets);

  // --- 5. l'offre ---------------------------------------------------------
  const total = document.createElement('p');
  total.className = 'offer__total';
  total.setAttribute('aria-live', 'polite');

  function majPrix(): void {
    total.replaceChildren();
    const unite = Number.parseFloat(choisi.price.amount);
    const paye = lotChoisi ? bundlePrice(lotChoisi, unite) : unite;
    const plein = lotChoisi
      ? bundleCompareAt(lotChoisi, unite)
      : choisi.compareAtPrice
        ? Number.parseFloat(choisi.compareAtPrice.amount)
        : null;

    const montant = document.createElement('strong');
    montant.textContent = formatMoney(paye, choisi.price.currencyCode);
    total.append(montant);
    if (plein !== null) {
      const ancien = document.createElement('s');
      ancien.textContent = formatMoney(plein, choisi.price.currencyCode);
      total.append(ancien);
    }
  }

  function carte(): HTMLLabelElement {
    const carte = document.createElement('label');
    carte.className = 'offer__choice';
    return carte;
  }

  function bloc(nom: string, note: string, prix: number, barre: number | null): HTMLElement {
    const texte = document.createElement('span');
    texte.className = 'offer__choice-text';
    const titre = document.createElement('span');
    titre.className = 'offer__choice-name';
    titre.textContent = nom;
    texte.append(titre);
    if (note) {
      const sous = document.createElement('span');
      sous.className = 'offer__choice-note';
      sous.textContent = note;
      texte.append(sous);
    }

    const zone = document.createElement('span');
    zone.className = 'offer__choice-price';
    const montant = document.createElement('strong');
    montant.textContent = formatMoney(prix, choisi.price.currencyCode);
    zone.append(montant);
    if (barre !== null) {
      const ancien = document.createElement('s');
      ancien.textContent = formatMoney(barre, choisi.price.currencyCode);
      zone.append(ancien);
    }

    const enveloppe = document.createElement('span');
    enveloppe.className = 'offer__choice-body';
    enveloppe.append(texte, zone);
    return enveloppe;
  }

  if (lots.length > 1) {
    // Lots Moon Bundles : c'est l'offre principale de la fiche du site.
    const legende = document.createElement('p');
    legende.className = 'offer__legend';
    legende.id = `offer-choix-${product.handle}`;
    legende.textContent = 'Votre choix';

    const groupe = document.createElement('div');
    groupe.className = 'offer__choices';
    groupe.setAttribute('role', 'radiogroup');
    groupe.setAttribute('aria-labelledby', legende.id);

    const unite = Number.parseFloat(choisi.price.amount);
    for (const lot of lots) {
      const ligne = carte();
      if (lot.misEnAvant) ligne.dataset['featured'] = 'true';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = `offer-${product.handle}`;
      radio.value = String(lot.quantite);
      radio.checked = lot === lotChoisi;
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        lotChoisi = lot;
        majPrix();
      });

      ligne.append(radio, bloc(lot.titre, lot.sousTitre, bundlePrice(lot, unite), bundleCompareAt(lot, unite)));

      if (lot.misEnAvant && lot.mentionMiseEnAvant) {
        const ruban = document.createElement('span');
        ruban.className = 'offer__ribbon';
        ruban.textContent = lot.mentionMiseEnAvant;
        ligne.append(ruban);
      }
      groupe.append(ligne);
    }
    element.append(legende, groupe);
  } else if (variants.length > 1) {
    // Pas de lot : les déclinaisons Shopify tiennent le rôle du choix.
    const legende = document.createElement('p');
    legende.className = 'offer__legend';
    legende.id = `offer-choix-${product.handle}`;
    legende.textContent = 'Votre choix';

    const groupe = document.createElement('div');
    groupe.className = 'offer__choices';
    groupe.setAttribute('role', 'radiogroup');
    groupe.setAttribute('aria-labelledby', legende.id);

    for (const variant of variants) {
      const ligne = carte();
      ligne.dataset['available'] = String(variant.availableForSale);

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = `offer-${product.handle}`;
      radio.value = variant.id;
      radio.checked = variant === choisi;
      radio.disabled = !variant.availableForSale;
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        choisi = variant;
        majPrix();
      });

      ligne.append(
        radio,
        bloc(
          variant.title,
          variant.availableForSale ? '' : 'Momentanément indisponible',
          Number.parseFloat(variant.price.amount),
          variant.compareAtPrice ? Number.parseFloat(variant.compareAtPrice.amount) : null,
        ),
      );
      groupe.append(ligne);
    }
    element.append(legende, groupe);
  }

  // Abonnement, quand Shopify en publie un.
  if (product.sellingPlan) {
    const ligne = document.createElement('label');
    ligne.className = 'offer__subscription';

    const case_ = document.createElement('input');
    case_.type = 'checkbox';
    case_.addEventListener('change', () => {
      abonnement = case_.checked;
    });

    const texte = document.createElement('span');
    texte.className = 'offer__choice-text';
    const nom = document.createElement('span');
    nom.className = 'offer__choice-name';
    nom.textContent = product.sellingPlan.name;
    texte.append(nom);
    if (product.sellingPlan.cadence) {
      const cadence = document.createElement('span');
      cadence.className = 'offer__choice-note';
      cadence.textContent = product.sellingPlan.cadence;
      texte.append(cadence);
    }

    ligne.append(case_, texte);
    element.append(ligne);
  }

  majPrix();
  element.append(total);

  return {
    element,
    selection: () => ({
      variant: choisi,
      quantite: lotChoisi?.quantite ?? 1,
      ...(lotChoisi && lotChoisi.quantite > 1 ? { lot: lotChoisi.titre } : {}),
      ...(abonnement && product.sellingPlan ? { sellingPlan: product.sellingPlan } : {}),
    }),
  };
}

/** 7. Les deux lignes de réassurance, paiement puis livraison. */
export function createReassurance(maintenant = new Date()): HTMLElement {
  const bloc = document.createElement('div');
  bloc.className = 'offer__reassurance';

  const paiement = document.createElement('p');
  paiement.append(document.createTextNode('Payer en 3x sans frais avec '));
  const klarna = document.createElement('span');
  klarna.className = 'offer__klarna';
  klarna.textContent = 'Klarna';
  paiement.append(klarna);

  const livraison = document.createElement('p');
  livraison.className = 'offer__delivery';
  livraison.append(document.createTextNode('Livraison '));
  const quand = document.createElement('strong');
  quand.textContent = deliveryWindow(maintenant);
  livraison.append(quand);

  bloc.append(paiement, livraison);
  return bloc;
}

/**
 * 8. La fiche en clair : ce que la boutique a rédigé pour ce produit, et rien
 * d'autre. Un produit sans ces metafields n'affiche pas la section, plutôt
 * qu'un texte générique qui ne lui correspondrait pas.
 */
export function createFaq(product: ShopProduct): HTMLElement | null {
  const details = product.details;
  const sections: { titre: string; texte: string }[] = [];
  if (details?.contenu) sections.push({ titre: 'Que contient ce pack ?', texte: details.contenu });
  if (details?.pourQuelChien) {
    sections.push({ titre: 'Pour quel type de chien ?', texte: details.pourQuelChien });
  }
  if (details?.conseils) {
    sections.push({ titre: 'Conseils d’utilisation', texte: details.conseils });
  }
  if (sections.length === 0) return null;

  const bloc = document.createElement('div');
  bloc.className = 'offer__faq';
  for (const { titre, texte } of sections) {
    const details_ = document.createElement('details');
    const resume = document.createElement('summary');
    resume.textContent = titre;
    const corps = document.createElement('div');
    corps.className = 'offer__faq-body';
    ajouterLignes(corps, texte);
    details_.append(resume, corps);
    bloc.append(details_);
  }
  return bloc;
}
